import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowRight, Zap, Shield, BarChart3, Users, Sparkles } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    document.title = "Smartorange HUB";
  }, []);

  const features = [
    {
      icon: BarChart3,
      title: "Project Analytics",
      description: "Full control over budgets, deadlines and team efficiency"
    },
    {
      icon: Users,
      title: "Team Management",
      description: "Artists, managers, partners — all in one system"
    },
    {
      icon: Shield,
      title: "Financial Tracking",
      description: "Automated payments, P&L reports, bank integration"
    },
    {
      icon: Zap,
      title: "AI Support",
      description: "Smart assistant for quick issue resolution"
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-orange-950/20 via-black to-black pointer-events-none" />
      
      {/* Animated circles */}
      <div className="fixed top-20 right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" />
      <div className="fixed bottom-20 left-20 w-72 h-72 bg-orange-600/10 rounded-full blur-3xl animate-pulse delay-1000" />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-8 py-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <BrandLogo className="h-10" />
        </div>
        <Button 
          onClick={() => navigate("/login")}
          className="bg-orange-500 hover:bg-orange-600 text-white px-6"
        >
          Sign In
        </Button>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 px-8 max-w-7xl mx-auto">
        <div className="pt-20 pb-32 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-orange-400" />
            <span className="text-sm text-orange-300">Project Management System</span>
          </div>

          {/* Main heading */}
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="text-zinc-50">SMARTORANGE</span>
            <br />
            <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
              HUB
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-zinc-400 max-w-2xl mx-auto mb-12">
            Centralized platform for CGI project management, 
            team coordination and finances
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => navigate("/login")}
              size="lg"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg group"
            >
              Get Started
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 pb-20">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:border-orange-500/30 transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4 group-hover:bg-orange-500/20 transition-colors">
                <feature.icon className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-zinc-50 mb-2">{feature.title}</h3>
              <p className="text-sm text-zinc-400">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="border-t border-zinc-800 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-orange-400 mb-2">78+</div>
              <div className="text-sm text-zinc-500">Projects</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-400 mb-2">50+</div>
              <div className="text-sm text-zinc-500">Artists</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-400 mb-2">₴16M+</div>
              <div className="text-sm text-zinc-500">Budget</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-400 mb-2">24/7</div>
              <div className="text-sm text-zinc-500">AI Support</div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-zinc-800 py-8 px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-6 opacity-50" />
            <span className="text-sm text-zinc-500">© 2026 Smartorange HUB</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <a href="https://smarto.agency" target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors">
              smarto.agency
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
